<template>
  <div>
    <CRow>
      <CCol>
        <h4 class="mb-3">Botón de emergencia</h4>
      </CCol>
    </CRow>

    <CAlert color="warning" class="mb-3">
      Pausar sitio bloqueará acceso al login. Para reanudar, usa la ruta pública <strong>/#/emergency/resume</strong> con token de emergencia.
    </CAlert>

    <CAlert color="danger" v-if="errorMessage" class="mb-3">
      {{ errorMessage }}
    </CAlert>

    <CAlert color="success" v-if="successMessage" class="mb-3">
      {{ successMessage }}
    </CAlert>

    <CCard class="mb-3">
      <CCardBody>
        <h5 class="mb-2">Estado actual</h5>
        <p class="mb-1">
          <strong>Envíos:</strong>
          <CBadge :color="status.sends_paused ? 'danger' : 'success'">
            {{ status.sends_paused ? 'Pausados' : 'Activos' }}
          </CBadge>
        </p>
        <p class="mb-0">
          <strong>Sitio:</strong>
          <CBadge :color="status.site_paused ? 'danger' : 'success'">
            {{ status.site_paused ? 'Pausado' : 'Activo' }}
          </CBadge>
        </p>
      </CCardBody>
    </CCard>

    <CRow>
      <CCol md="6" class="mb-3">
        <CCard class="h-100">
          <CCardBody>
            <h5>Pausar envíos</h5>
            <p>Detiene envíos manuales y automáticos de WhatsApp/correos ligados a envíos.</p>
            <CButton
              v-if="!status.sends_paused"
              color="danger"
              block
              :disabled="loading"
              @click="pauseSends"
            >
              Pausar envíos
            </CButton>
            <CButton
              v-else
              color="success"
              block
              :disabled="loading"
              @click="resumeSends"
            >
              Reanudar envíos
            </CButton>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol md="6" class="mb-3">
        <CCard class="h-100 border-danger">
          <CCardBody>
            <h5>Pausar sitio</h5>
            <p>Entra en mantenimiento y bloquea login. También activa pausa de envíos.</p>
            <CButton
              color="dark"
              block
              :disabled="loading || status.site_paused"
              @click="openPauseSiteModal"
            >
              {{ status.site_paused ? 'Sitio ya pausado' : 'Pausar sitio ahora' }}
            </CButton>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CModal
      :show.sync="pauseSiteModal"
      title="Confirmar pausa del sitio"
      color="dark"
      class="basic-modal-color"
    >
      <p class="mb-2">
        Esta acción pausará el sitio completo, bloqueará el login y dejará solo la ruta de reanudación.
      </p>
      <p class="mb-0 text-danger">
        También se pausarán todos los envíos automáticos y manuales.
      </p>

      <template #footer>
        <CButton color="secondary" @click="pauseSiteModal = false" :disabled="loading">Cancelar</CButton>
        <CButton color="dark" @click="confirmPauseSite" :disabled="loading">Sí, pausar sitio</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import emergencyService from '@/services/admin_emergency'
import store from '@/store'

export default {
  name: 'EmergencyControl',
  data() {
    return {
      loading: false,
      status: {
        sends_paused: false,
        site_paused: false,
      },
      pauseSiteModal: false,
      errorMessage: '',
      successMessage: '',
    }
  },
  async mounted() {
    await this.loadStatus()
  },
  methods: {
    async loadStatus() {
      this.errorMessage = ''
      this.successMessage = ''
      this.loading = true
      this.loadingGlobal()

      try {
        const response = await emergencyService.status()
        this.status = {
          sends_paused: !!(response && response.data && response.data.sends_paused),
          site_paused: !!(response && response.data && response.data.site_paused),
        }
      } catch (error) {
        this.errorMessage = (error && error.response && error.response.data && error.response.data.message)
          ? error.response.data.message
          : 'No se pudo consultar el estado de emergencia.'
      } finally {
        this.loading = false
        this.loadedGlobal()
      }
    },
    async pauseSends() {
      this.errorMessage = ''
      this.successMessage = ''
      this.loading = true
      this.loadingGlobal()

      try {
        const response = await emergencyService.pauseSends()
        this.status = response.data || this.status
        this.successMessage = response.message || 'Envíos pausados.'
      } catch (error) {
        this.errorMessage = (error && error.response && error.response.data && error.response.data.message)
          ? error.response.data.message
          : 'No se pudo pausar envíos.'
      } finally {
        this.loading = false
        this.loadedGlobal()
      }
    },
    async resumeSends() {
      this.errorMessage = ''
      this.successMessage = ''
      this.loading = true
      this.loadingGlobal()

      try {
        const response = await emergencyService.resumeSends()
        this.status = response.data || this.status
        this.successMessage = response.message || 'Envíos reanudados.'
      } catch (error) {
        this.errorMessage = (error && error.response && error.response.data && error.response.data.message)
          ? error.response.data.message
          : 'No se pudo reanudar envíos.'
      } finally {
        this.loading = false
        this.loadedGlobal()
      }
    },
    openPauseSiteModal() {
      this.pauseSiteModal = true
    },
    async confirmPauseSite() {
      this.pauseSiteModal = false

      this.errorMessage = ''
      this.successMessage = ''
      this.loading = true
      this.loadingGlobal()

      try {
        const response = await emergencyService.pauseSite()
        this.status = response.data || this.status
        this.successMessage = response.message || 'Sitio pausado.'

        localStorage.removeItem('token')
        window.location.hash = '/maintenance'
      } catch (error) {
        this.errorMessage = (error && error.response && error.response.data && error.response.data.message)
          ? error.response.data.message
          : 'No se pudo pausar el sitio.'
      } finally {
        this.loading = false
        this.loadedGlobal()
      }
    },
    loadingGlobal() {
      store.commit('loading')
    },
    loadedGlobal() {
      store.commit('loaded')
    },
  },
}
</script>
